import {
  getAsYouType,
  getCountryCodeForRegionCode,
  getSupportedRegionCodes,
  parsePhoneNumber,
} from "awesome-phonenumber";
import { Flex, Select, Text } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import clsx from "clsx";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { translateIdentity, type StaticTextTranslator } from "../../utils";
import { PHONE_INPUT_TEST_IDS } from "./PhoneInput.testIds";
import styles from "./PhoneInput.module.css";

const REGION_CODES = getSupportedRegionCodes();
const REGION_CALLING_CODE_MAP = new Map(
  REGION_CODES.map((regionCode) => [
    regionCode,
    getCountryCodeForRegionCode(regionCode),
  ]),
);

const DEFAULT_REGION_CODE = "US";

const regionSelectOptions = REGION_CODES.map((regionCode) => ({
  label: `${regionCode} +${REGION_CALLING_CODE_MAP.get(regionCode)}`,
  value: regionCode,
}));

function formatPhoneNumber(digits: string, regionCode: string) {
  const callingCode = REGION_CALLING_CODE_MAP.get(regionCode) ?? 1;
  const prefix = `+${callingCode}`;
  const unformatted = digits.replace(/\D/g, "");

  if (!unformatted) {
    return { display: "", formatted: "", valid: false };
  }

  const ayt = getAsYouType(regionCode);
  const formatted = ayt.reset(`${prefix}${unformatted}`);
  const display = formatted.slice(prefix.length).trimStart();

  return { display, formatted, valid: ayt.getPhoneNumber().valid };
}

interface PhoneInputProps {
  onValueChange: (formattedValue: string, isValid: boolean) => void;
  onKeyDown: (
    e: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  inputRef: (element: HTMLInputElement | null) => void;
  disabled?: boolean;
  /** Seed for edit-mode prefill; read once at mount (parent key-remounts). */
  initialValue?: string;
  setRegionSelectorRef?: (element: HTMLInputElement | null) => void;
  translate?: StaticTextTranslator;
}

export function PhoneInput({
  onValueChange,
  onKeyDown,
  inputRef,
  disabled = false,
  initialValue,
  setRegionSelectorRef,
  translate = translateIdentity,
}: PhoneInputProps) {
  const localRef = useRef<HTMLInputElement | null>(null);
  // Edit-mode prefill: infer region + national digits from the stored
  // value once at mount (the library does the region math). Stored answers
  // are E.164 ("+12125564317"); a bare national number is parsed against
  // the default region — never promoted to international by prepending
  // "+", which would misread "2125564317" as Morocco (+212...).
  const [seed] = useState(() => {
    if (!initialValue) {
      return null;
    }
    const raw = initialValue.trim();
    const parsed = raw.startsWith("+")
      ? parsePhoneNumber(raw)
      : parsePhoneNumber(raw, { regionCode: DEFAULT_REGION_CODE });
    if (!parsed.valid || !parsed.regionCode) {
      // Unparseable stored value: prefill nothing rather than a garbled
      // guess the candidate would have to notice and undo.
      return null;
    }
    return {
      region: parsed.regionCode,
      display: formatPhoneNumber(
        parsed.number?.significant ?? "",
        parsed.regionCode,
      ),
    };
  });
  const [regionCode, setRegionCode] = useState(
    seed?.region ?? DEFAULT_REGION_CODE,
  );
  const [displayValue, setDisplayValue] = useState(seed?.display.display ?? "");

  const updatePhoneValue = useCallback(
    (digits: string, region: string) => {
      const { display, formatted, valid } = formatPhoneNumber(digits, region);
      setDisplayValue(display);
      onValueChange(formatted, valid);
    },
    [onValueChange],
  );

  // Report the seeded value upward once so the parent's submit value and
  // validity match what's displayed. Mount-only by design (key-remount).
  // When a provided initialValue was REJECTED (seed === null), push an
  // empty/invalid value instead — the parent seeded its own state from the
  // raw string and must not submit a value this input never displayed.
  const hasReportedSeedRef = useRef(false);
  useEffect(() => {
    if (hasReportedSeedRef.current) {
      return;
    }
    hasReportedSeedRef.current = true;
    if (seed) {
      onValueChange(seed.display.formatted, seed.display.valid);
    } else if (initialValue) {
      onValueChange("", false);
    }
  }, [seed, initialValue, onValueChange]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updatePhoneValue(e.target.value, regionCode);
    },
    [regionCode, updatePhoneValue],
  );

  const handleRegionChange = useCallback(
    (value: string | null) => {
      if (!value) return;
      setRegionCode(value);
      updatePhoneValue(displayValue, value);
      localRef.current?.focus();
    },
    [displayValue, updatePhoneValue],
  );

  return (
    <Flex className={styles.inputWrapper}>
      <Select
        allowDeselect={false}
        classNames={{
          dropdown: styles.regionSelectorDropdown,
          input: clsx(styles.regionSelector, styles.regionSelectorInput),
          root: styles.regionSelectorRoot,
          section: styles.regionSelectorSection,
          wrapper: styles.regionSelector,
        }}
        data={regionSelectOptions}
        data-testid={PHONE_INPUT_TEST_IDS.regionSelector}
        value={regionCode}
        disabled={disabled}
        nothingFoundMessage={translate("Region or code not found")}
        onChange={handleRegionChange}
        ref={setRegionSelectorRef}
        renderOption={({ checked, option }) => (
          <Flex
            align="center"
            className={styles.regionSelectorOption}
            justify="space-between"
          >
            <Flex>
              <Text mr="sm">{option.value}</Text>
              <Text className={styles.regionSelectorOptionCallingCode}>
                +{REGION_CALLING_CODE_MAP.get(option.value)}
              </Text>
            </Flex>
            {checked && (
              <IconCheck
                className={styles.regionSelectorOptionCheck}
                size={16}
              />
            )}
          </Flex>
        )}
        size="md"
        styles={{
          input: {
            backgroundColor: "transparent",
            border: "none",
            lineHeight: "22px",
            paddingLeft: 0,
            paddingRight: 28,
          },
          label: { lineHeight: "22px" },
          root: { backgroundColor: "transparent" },
          section: { backgroundColor: "transparent" },
          wrapper: { backgroundColor: "transparent", border: "none" },
        }}
        variant="unstyled"
      />
      <input
        className={styles.input}
        data-testid={PHONE_INPUT_TEST_IDS.phone}
        inputMode="numeric"
        name="phone-number"
        disabled={disabled}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        placeholder={translate("Enter digits only")}
        ref={(el: HTMLInputElement | null) => {
          localRef.current = el;
          inputRef(el);
        }}
        type="tel"
        value={displayValue}
      />
    </Flex>
  );
}
